import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Coins } from "lucide-react";
import { Wordmark } from "./Logo";
import { Ticker } from "./Ticker";
import { NAV, TOOLS, selectMode } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const link = "text-sm text-slate-300 hover:text-white transition-colors";

export const openTool = (id) => {
  selectMode(id);
  scrollToId("order");
};

const ToolsMenu = ({ onPick }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger data-testid="nav-tools-trigger" className={`${link} flex items-center gap-1 outline-none`}>
      Tools <ChevronDown className="h-3.5 w-3.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-64 bg-[#0b0b0d] border-white/10 p-1.5">
      {TOOLS.map((t) => (
        <DropdownMenuItem key={t.id} data-testid={`nav-tool-${t.id}`} onSelect={() => onPick(t.id)} className="flex flex-col items-start gap-0.5 rounded-md px-3 py-2.5 cursor-pointer focus:bg-[#3b4049]/15">
          <span className="text-sm font-medium text-white">{t.label}</span>
          <span className="text-xs text-slate-400">{t.note}</span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const Account = ({ mobile = false }) => {
  const { user, openAuth } = useAuth();
  const p = mobile ? "nav-mobile" : "nav";
  if (user) {
    const admin = user.role === "admin";
    return (
      <div className={`flex items-center gap-2 ${mobile ? "flex-col items-stretch" : ""}`}>
        <span data-testid={`${p}-credits`} className="inline-flex items-center justify-center gap-1.5 text-sm text-slate-200 px-3 py-2 rounded-lg border border-white/10">
          <Coins className="h-4 w-4 text-[#d5d9df]" /> {user.unlimited ? "Unlimited" : user.credits ?? 0}
        </span>
        {!user.unlimited && (user.free_left ?? 0) > 0 && (
          <span data-testid={`${p}-free-left`} className="inline-flex items-center justify-center text-xs font-semibold text-black bg-[#e6e8ec] px-2.5 py-2 rounded-lg">
            {user.free_left} free
          </span>
        )}
        <Link to={admin ? "/admin" : "/my-orders"} data-testid={`${p}-account-orders`} className="btn-line px-4 py-2.5">
          {admin ? "Dashboard" : "My Orders"}
        </Link>
      </div>
    );
  }
  return (
    <div className={`flex gap-2 ${mobile ? "flex-col" : ""}`}>
      <button data-testid={`${p}-login`} onClick={() => openAuth("login")} className="btn-line px-4 py-2.5">Log in</button>
      <button data-testid={`${p}-signup`} onClick={() => openAuth("register")} className="btn-solid px-4 py-2.5">Sign up</button>
    </div>
  );
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };
  const pick = (id) => {
    setOpen(false);
    openTool(id);
  };

  return (
    <header data-testid="site-nav" className="fixed top-0 inset-x-0 z-50">
      <div className="bg-[#050505]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-10">
            <Wordmark onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
            <nav className="hidden lg:flex items-center gap-8">
              <ToolsMenu onPick={pick} />
              {NAV.map((n) => (
                <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n.id)} className={link}>{n.label}</button>
              ))}
            </nav>
          </div>
          <div className="hidden md:block"><Account /></div>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu" className="md:hidden h-10 w-10 grid place-items-center rounded-lg border border-white/15">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <Ticker />
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-5 pb-6 max-h-[calc(100svh-100px)] overflow-y-auto">
          <div className="eyebrow pt-5 pb-2">Tools</div>
          {TOOLS.map((t) => (
            <button key={t.id} data-testid={`nav-mobile-tool-${t.id}`} onClick={() => pick(t.id)} className="block w-full text-left py-3 border-b border-white/5">
              <span className="font-medium">{t.label}</span> <span className="text-sm text-slate-400">· {t.note}</span>
            </button>
          ))}
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-mobile-link-${n.id}`} onClick={() => go(n.id)} className="block w-full text-left py-3.5 border-b border-white/5 font-medium">{n.label}</button>
          ))}
          <div className="mt-5"><Account mobile /></div>
        </div>
      )}
    </header>
  );
};
